import React from 'react'
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import sv from 'date-fns/locale/sv'
import {formatDate} from '../../common/utils/dateUtils'
import {Box} from '../../webui/Box'
import {Button} from '../../webui/Button'
import {Checkbox} from '../../webui/Checkbox'
import {MenuItem} from '../../webui/inputs/MenuItem'
import {Select} from '../../webui/inputs/Select'
import {Switch} from '../../webui/inputs/Switch'
import {TextField} from '../../webui/inputs/TextField'
import {INPUT_WIDTH} from '../../webui/inputs/utils'
import {Typography} from '../../webui/Typography'

export interface FilterOption {
  label: React.ReactNode
  value: string
}

export type FilterConfig<T> = {
  [sectionKey: string]: {
    label?: string
    fields: {
      [K in keyof Partial<T>]:
        | {label: string; type: 'string'}
        | {label: string; type: 'multiSelect'; options: FilterOption[]}
        | {label: string; type: 'boolean'}
        | {label: string; type: 'date'}
    }
  }
}

/**
 * MultiSelect values are always string[] because FilterOption.value is string.
 * This keeps TableFilter type-safe and avoids “generic but actually string” casts.
 */
export type FilterValue<T> = T extends any[] ? {value: string[]; include: boolean} : {value: T}

type FilterProps<T> = {
  filters: {[K in keyof T]?: FilterValue<T[K]>}
  onClearFilter?: () => void
  onFilterChange: (filters: {[K in keyof T]?: FilterValue<T[K]>}) => void
  filtersConfig: FilterConfig<T>
}

const asStringArray = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.map(String)
  if (v === null || v === undefined || v === '') return []
  return [String(v)]
}

export const TableFilter = <T extends Record<string, unknown>>({filters, onClearFilter, onFilterChange, filtersConfig}: FilterProps<T>) => {
  const handleChange = (key: keyof T, value: unknown) => {
    const config = Object.values(filtersConfig)
      .flatMap((section) => Object.entries(section.fields))
      .find(([fieldKey]) => fieldKey === key)?.[1]

    if (!config) return

    onFilterChange({
      ...filters,
      [key]:
        config.type === 'multiSelect'
          ? {
              value: asStringArray(value),
              include: (filters[key] as {include: boolean})?.include ?? true,
            }
          : ({value} as FilterValue<T[keyof T]>),
    })
  }

  const toggleIncludeExclude = (key: keyof T) => {
    onFilterChange({
      ...filters,
      [key]: {
        ...(filters[key] as {value: string[]; include: boolean}),
        include: !(filters[key] as {include: boolean}).include,
      },
    })
  }

  const handleBooleanToggle = (key: keyof T) => {
    const currentValue = (filters[key] as {value: any} | undefined)?.value
    const newValue = currentValue === true ? false : currentValue === false ? null : true

    onFilterChange({
      ...filters,
      [key]: {value: newValue} as FilterValue<T[keyof T]>,
    })
  }

  const handleDateChange = (key: keyof T, value: Date | null) => {
    onFilterChange({
      ...filters,
      [key]: {value: value ? formatDate(value) : null} as FilterValue<T[keyof T]>,
    })
  }

  return (
    <Box display={'flex'} flexDirection={'column'} gap={1}>
      {Object.entries(filtersConfig).map(([sectionKey, section]) => (
        <div key={sectionKey}>
          {section.label && <Typography>{section.label}</Typography>}

          <div style={{display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
            {Object.entries(section.fields).map(([key, config]) => {
              const typedKey = key as keyof T
              const filterData = filters[typedKey]

              if (!filterData) return null

              return (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  {config.type === 'boolean' && (
                    <>
                      <Checkbox
                        indeterminate={(filterData as any).value === null}
                        checked={(filterData as any).value === true}
                        onChange={() => handleBooleanToggle(typedKey)}
                      />
                      <label>{config.label}</label>
                    </>
                  )}

                  {config.type === 'multiSelect' && (
                    <Box display="flex" flexDirection={'column'}>
                      <Typography>{config.label}</Typography>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Select
                          multiple
                          fullWidth
                          value={asStringArray((filterData as any).value)}
                          onChange={(e) => {
                            const raw = e.target.value
                            const values = asStringArray(raw)
                            if (values.includes('__all__')) {
                              handleChange(typedKey, [])
                            } else {
                              handleChange(typedKey, raw)
                            }
                          }}
                          displayEmpty
                          renderValue={(selected) => {
                            const values = asStringArray(selected)
                            return values.length > 0
                              ? values.map((s) => config.options.find((option) => option.value === s)?.label).filter(Boolean)
                              : 'All'
                          }}
                          variant={'outlined'}
                        >
                          <MenuItem value="__all__">
                            <em>All</em>
                          </MenuItem>
                          {config.options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>

                        <Box alignItems={'center'} display={'flex'} flexDirection={'row'}>
                          <Typography>Exclude</Typography>
                          <Switch label={'Include'} onChange={() => toggleIncludeExclude(typedKey)} value={(filterData as any).include} />
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {config.type === 'date' && (
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sv}>
                      <DatePicker
                        label={config.label}
                        value={(filterData as any).value ? new Date((filterData as any).value as string) : null}
                        onChange={(value) => handleDateChange(typedKey, value)}
                        sx={{minWidth: INPUT_WIDTH}}
                      />
                    </LocalizationProvider>
                  )}

                  {config.type === 'string' && (
                    <TextField
                      label={config.label}
                      value={(filterData as any).value as string}
                      onChange={(value) => handleChange(typedKey, value)}
                      variant={'outlined'}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ))}

      <Box alignSelf={'flex-end'}>
        <Button onClick={onClearFilter}>Clear filter</Button>
      </Box>
    </Box>
  )
}
